import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { contaApi } from '../../services/api';
import { useClienteAuth } from '../../contexts/ClienteAuthContext';
import { Spinner } from '../../components/ui';
import { formatarTelefone } from '../../lib/format';
import { Eye, EyeOff, Lock, ShieldCheck, User, Phone, Mail, BadgeCheck } from 'lucide-react';

function CampoSenha({
  id, rotulo, valor, onChange, autoComplete,
}: {
  id: string; rotulo: string; valor: string; onChange: (v: string) => void; autoComplete?: string;
}) {
  const [mostrar, setMostrar] = useState(false);
  return (
    <div>
      <label className="input-label" htmlFor={id}>{rotulo}</label>
      <div className="relative">
        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
        <input
          id={id}
          type={mostrar ? 'text' : 'password'}
          value={valor}
          onChange={e => onChange(e.target.value)}
          placeholder="••••••••"
          className="input-field !pl-10 pr-11"
          autoComplete={autoComplete}
          required
        />
        <button
          type="button"
          onClick={() => setMostrar(!mostrar)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 icon-btn"
          aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {mostrar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function Feedback({ erro, ok }: { erro: string; ok: string }) {
  if (erro) {
    return (
      <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700 animate-fade-in">
        {erro}
      </div>
    );
  }
  if (ok) {
    return (
      <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-[13px] font-medium text-emerald-700 animate-fade-in">
        {ok}
      </div>
    );
  }
  return null;
}

export function ContaSegurancaPage() {
  const { cliente, atualizarCliente } = useClienteAuth();

  // ── Dados da conta ─────────────────────────────────────
  const [nome, setNome] = useState(cliente?.nome || '');
  const [telefone, setTelefone] = useState(cliente?.telefone || '');
  const [erroPerfil, setErroPerfil] = useState('');
  const [okPerfil, setOkPerfil] = useState('');

  const salvarPerfil = useMutation({
    mutationFn: () => contaApi.atualizarPerfil({ nome: nome.trim(), telefone: telefone.replace(/\D/g, '') }),
    onSuccess: (res) => {
      atualizarCliente(res.data);
      setOkPerfil('Dados atualizados com sucesso.');
      setErroPerfil('');
    },
    onError: (err: any) => {
      setErroPerfil(err.response?.data?.error || 'Não foi possível salvar os dados.');
      setOkPerfil('');
    },
  });

  // ── Troca de senha ─────────────────────────────────────
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [erroSenha, setErroSenha] = useState('');
  const [okSenha, setOkSenha] = useState('');

  const trocarSenha = useMutation({
    mutationFn: () => contaApi.trocarSenha(senhaAtual, novaSenha),
    onSuccess: () => {
      setOkSenha('Senha alterada com sucesso.');
      setErroSenha('');
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmar('');
    },
    onError: (err: any) => {
      setErroSenha(err.response?.data?.error || 'Não foi possível trocar a senha.');
      setOkSenha('');
    },
  });

  const handleTrocarSenha = (e: React.FormEvent) => {
    e.preventDefault();
    setErroSenha('');
    setOkSenha('');
    if (novaSenha.length < 6) {
      setErroSenha('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (novaSenha !== confirmar) {
      setErroSenha('A confirmação não confere com a nova senha.');
      return;
    }
    trocarSenha.mutate();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink-950 tracking-tight">Conta e segurança</h1>
        <p className="text-sm text-ink-500 mt-1">Gerencie seus dados pessoais e sua senha.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* ── Dados da conta ── */}
        <form
          onSubmit={(e) => { e.preventDefault(); setErroPerfil(''); setOkPerfil(''); salvarPerfil.mutate(); }}
          className="card space-y-4"
        >
          <h2 className="card-title flex items-center gap-2">
            <User className="w-4 h-4 text-brand-600" /> Meus dados
          </h2>

          <div>
            <label className="input-label" htmlFor="pf-nome">Nome completo</label>
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                id="pf-nome" type="text" value={nome}
                onChange={e => setNome(e.target.value)}
                className="input-field !pl-10" autoComplete="name" required
              />
            </div>
          </div>

          <div>
            <label className="input-label" htmlFor="pf-tel">Telefone / WhatsApp</label>
            <div className="relative">
              <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                id="pf-tel" type="tel" value={formatarTelefone(telefone) === '—' ? telefone : telefone}
                onChange={e => setTelefone(e.target.value)}
                placeholder="(44) 99999-9999"
                className="input-field !pl-10" autoComplete="tel" required
              />
            </div>
          </div>

          <div>
            <label className="input-label" htmlFor="pf-email">E-mail</label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                id="pf-email" type="email" value={cliente?.email || ''}
                className="input-field !pl-10 opacity-60 cursor-not-allowed"
                disabled
              />
            </div>
            <p className="flex items-center gap-1.5 text-2xs text-emerald-600 font-semibold mt-1.5">
              <BadgeCheck className="w-3.5 h-3.5" /> E-mail verificado — não é possível alterá-lo.
            </p>
          </div>

          <Feedback erro={erroPerfil} ok={okPerfil} />

          <button type="submit" disabled={salvarPerfil.isPending} className="btn-primary w-full">
            {salvarPerfil.isPending ? <Spinner className="h-5 w-5 !border-white/30 !border-t-white" /> : 'Salvar dados'}
          </button>
        </form>

        {/* ── Trocar senha ── */}
        <form onSubmit={handleTrocarSenha} className="card space-y-4">
          <h2 className="card-title flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-brand-600" /> Trocar senha
          </h2>

          <CampoSenha id="sn-atual" rotulo="Senha atual" valor={senhaAtual} onChange={setSenhaAtual} autoComplete="current-password" />
          <CampoSenha id="sn-nova" rotulo="Nova senha (mín. 6 caracteres)" valor={novaSenha} onChange={setNovaSenha} autoComplete="new-password" />
          <CampoSenha id="sn-confirma" rotulo="Confirmar nova senha" valor={confirmar} onChange={setConfirmar} autoComplete="new-password" />

          <Feedback erro={erroSenha} ok={okSenha} />

          <button type="submit" disabled={trocarSenha.isPending} className="btn-primary w-full">
            {trocarSenha.isPending ? <Spinner className="h-5 w-5 !border-white/30 !border-t-white" /> : 'Alterar senha'}
          </button>

          <p className="text-2xs text-ink-400 leading-relaxed">
            Esqueceu a senha atual? Saia da conta e use a opção <strong>“Esqueci minha senha”</strong> na
            tela de login para redefini-la por e-mail.
          </p>
        </form>
      </div>
    </div>
  );
}
